import gsap from "gsap";
import { MeshLineGeometry, MeshLineMaterial } from "meshline";
import * as THREE from "three";
import type Camera from "../Camera";
import Experience from "../Experience";
import type Renderer from "../Renderer";
import type Resources from "../utils/Resources";

export interface LinePoints {
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  /** optional relative width multiplier (1 = base) */
  width?: number;
}

export class CoolS2D {
  public meshes: THREE.Mesh[] = [];
  public group = new THREE.Group();
  public lineMaterials: MeshLineMaterial[] = [];

  // base pixel width for lines (screen-space)
  private BASE_PX_WIDTH = 4;

  private lines: LinePoints[] = [
    // first 3 vertical lines
    {
      startPoint: new THREE.Vector3(-2, 0, 0),
      endPoint: new THREE.Vector3(-2, 1, 0),
    },
    {
      startPoint: new THREE.Vector3(0, 0, 0),
      endPoint: new THREE.Vector3(0, 1, 0),
    },
    {
      startPoint: new THREE.Vector3(2, 0, 0),
      endPoint: new THREE.Vector3(2, 1, 0),
    },
    // second vertical lines
    {
      startPoint: new THREE.Vector3(-2, -1, 0),
      endPoint: new THREE.Vector3(-2, -2, 0),
    },
    {
      startPoint: new THREE.Vector3(0, -1, 0),
      endPoint: new THREE.Vector3(0, -2, 0),
    },
    {
      startPoint: new THREE.Vector3(2, -1, 0),
      endPoint: new THREE.Vector3(2, -2, 0),
    },
    // top ^
    {
      startPoint: new THREE.Vector3(-2, 1, 0),
      endPoint: new THREE.Vector3(0, 2, 0),
      width: 1.5,
    },
    {
      startPoint: new THREE.Vector3(0, 2, 0),
      endPoint: new THREE.Vector3(2, 1, 0),
      width: 1.5,
    },
    // bottom
    {
      startPoint: new THREE.Vector3(-2, -2, 0),
      endPoint: new THREE.Vector3(0, -3, 0),
      width: 1.5,
    },
    {
      startPoint: new THREE.Vector3(0, -3, 0),
      endPoint: new THREE.Vector3(2, -2, 0),
      width: 1.5,
    },

    // mid cross lines
    {
      startPoint: new THREE.Vector3(-2, 0, 0),
      endPoint: new THREE.Vector3(0, -1, 0),
      width: 1.2,
    },
    {
      startPoint: new THREE.Vector3(0, 0, 0),
      endPoint: new THREE.Vector3(2, -1, 0),
      width: 1.2,
    },

    {
      startPoint: new THREE.Vector3(2, 0, 0),
      endPoint: new THREE.Vector3(1, -0.5, 0),
      width: 1.2,
    },
    {
      startPoint: new THREE.Vector3(-1, -0.5, 0),
      endPoint: new THREE.Vector3(-2, -1, 0),
      width: 1.2,
    },
  ];

  private experience: Experience;
  private scene: THREE.Scene;
  private renderer: Renderer;
  private camera: Camera;
  private resources: Resources;
  private pencilTexture: THREE.Texture;

  constructor() {
    this.experience = new Experience();
    this.renderer = this.experience.renderer;
    this.camera = this.experience.camera;
    this.resources = this.experience.resources;
    this.scene = this.experience.scene;

    this.pencilTexture = this.resources.get<THREE.Texture>("pencilTexture")!;
    this.initializeTexture();

    // a pixel-consistent twist magnitude basis (convert px -> world)
    const vis = this.camera.getVisibleSizeAtZ(0);
    const pxToWorld = vis.height / (this.renderer.height || 1);

    // Build meshes
    this.lines.forEach((line, i) => {
      const geometry = new MeshLineGeometry();

      let points = [line.startPoint.clone()];
      // if (!line.startPoint.equals(line.endPoint)) {
      //   points.push(line.startPoint.clone().lerp(line.endPoint, 0.5));
      // }

      const magPx = THREE.MathUtils.randFloat(1.0, 2.0); // 1–2 px feel
      const magWorld = magPx * pxToWorld;

      const midPoints = this.getMidPoints(
        line.startPoint,
        line.endPoint,
        magWorld
      );

      points = [...points, ...midPoints];

      points.push(line.endPoint.clone());
      geometry.setPoints(points, () => 1);

      const mat = new MeshLineMaterial({
        // Screen-space width mode:
        sizeAttenuation: 0,
        lineWidth: this.pxWidth(line.width), // pixels

        resolution: new THREE.Vector2(
          this.renderer.width,
          this.renderer.height
        ),

        // Pencil texturing:
        useMap: 1,
        map: this.pencilTexture,
        // color: new THREE.Color("#2c2c2c"),
        alphaTest: 0.005,

        // Reveal animation (same as your prior approach):
        visibility: 0.0,

        // Keep solid line (we’re using visibility, not dash draw-on)
        dashArray: 0,
        dashOffset: 0,
        dashRatio: 0,
      });

      // Rendering niceties
      mat.transparent = true;
      mat.depthTest = false; // Disable depth test to prevent flickering
      mat.depthWrite = false;

      this.lineMaterials.push(mat);

      const mesh = new THREE.Mesh(geometry, mat);
      mesh.frustumCulled = true;
      mesh.renderOrder = 10 + i; // ensure consistent overdraw order if you layer strokes

      this.meshes.push(mesh);
      this.group.add(mesh);
    });
    this.scene.add(this.group);
    this.resize();
  }

  private initializeTexture() {
    this.pencilTexture.wrapS = THREE.RepeatWrapping;
    this.pencilTexture.wrapT = THREE.RepeatWrapping;
    this.pencilTexture.repeat.set(6, 1); // pack more graphite along the stroke
    this.pencilTexture.anisotropy = 8; // safe default; you can set renderer cap externally if you prefer
    this.pencilTexture.minFilter = THREE.LinearMipmapLinearFilter;
    this.pencilTexture.magFilter = THREE.LinearFilter;
    this.pencilTexture.generateMipmaps = true;
    // If textures loaded after materials are created, flag updates:
    this.lineMaterials.forEach((m) => (m.needsUpdate = false));
  }

  /** Turn an optional relative width into pixel width for screen-space line */
  private pxWidth(mult = 1) {
    const vw = Math.min(this.renderer.width, this.renderer.height); // device px
    // 0.7% of the short side, clamped between 6 and 24 px as a base
    const responsive = THREE.MathUtils.clamp(Math.round(vw * 0.015), 6, 24);
    return responsive * mult * this.BASE_PX_WIDTH;
  }

  /** Must be called on window resize (you already do from main.ts) */
  resize() {
    for (const [i, m] of this.lineMaterials.entries()) {
      m.resolution.set(this.renderer.width, this.renderer.height);
      const mult = this.lines[i].width ?? 1;
      m.lineWidth = this.pxWidth(mult);
    }
    this.centerAndFit();
  }

  // Center the group's content at (0,0, z=0) and scale it to fit with a margin
  private centerAndFitGroupToView(opts?: { margin?: number; zPlane?: number }) {
    const margin = opts?.margin ?? 0.9;
    const zPlane = opts?.zPlane ?? 0;

    this.group.scale.setScalar(1);
    const box = new THREE.Box3().setFromObject(this.group);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);
    this.group.children.forEach((child) => child.position.sub(center));

    const vis = this.camera.getVisibleSizeAtZ(zPlane);
    const targetW = vis.width * margin;
    const targetH = vis.height * margin;
    const sX = size.x > 0 ? targetW / size.x : 1;
    const sY = size.y > 0 ? targetH / size.y : 1;
    const scale = Math.min(sX, sY);

    this.group.position.set(0, 0, zPlane);
    this.group.scale.setScalar(scale);
  }

  centerAndFit() {
    this.centerAndFitGroupToView({
      margin: 0.9,
      zPlane: 0.1,
    });
  }

  private getMidPoints(a: THREE.Vector3, b: THREE.Vector3, magWorld: number) {
    const dir = new THREE.Vector2(b.x - a.x, b.y - a.y).normalize();
    const n = new THREE.Vector2(-dir.y, dir.x); // perpendicular
    const p1 = a.clone().lerp(b, 1 / 3);
    const p2 = a.clone().lerp(b, 2 / 3);
    const t1 = new THREE.Vector3(
      p1.x + n.x * magWorld,
      p1.y + n.y * magWorld,
      p1.z
    );
    const t2 = new THREE.Vector3(
      p2.x - n.x * magWorld,
      p2.y - n.y * magWorld,
      p2.z
    );
    return [t1, t2];
  }

  public getLineCount() {
    return this.lineMaterials.length;
  }

  public setVisibility(index: number, v: number) {
    const m = this.lineMaterials[index];
    if (!m) return;
    m.visibility = Math.max(0, Math.min(1, v));
    m.needsUpdate = true;
  }

  public makeLinesFadeable() {
    this.meshes.forEach((mesh) => {
      (mesh.material as THREE.Material).depthWrite = true;
      (mesh.material as THREE.Material).depthTest = true;
    });
  }

  public crumbleDissolve(duration: number): Promise<void> {
    return new Promise((resolve) => {
      const timeline = gsap.timeline({
        onComplete: resolve,
      });

      // Scale up to compensate for perspective as it moves away
      const scaleFactor = 10; // Tweaking knob: adjust this to control how much bigger it gets
      timeline.to(
        this.group.scale,
        {
          x: this.group.scale.x * scaleFactor,
          y: this.group.scale.y * scaleFactor,
          z: this.group.scale.z * scaleFactor,
          duration: duration,
          ease: "power2.out",
        },
        0
      );

      // Animate each mesh with vortex effect
      this.crumbleShatter(duration, timeline);
    });
  }

  private crumbleShatter(duration: number, timeline: gsap.core.Timeline) {
    this.meshes.forEach((mesh, i) => {
      const randomX = THREE.MathUtils.randFloatSpread(3);
      const randomY = THREE.MathUtils.randFloatSpread(3);
      const randomRotation = THREE.MathUtils.randFloatSpread(Math.PI);
      const mat = this.lineMaterials[i];

      // Delay the tearing apart by 6 seconds, then stagger each line
      const delay = 4 + i * 0.05;

      timeline.to(
        mesh.position,
        {
          x: mesh.position.x + randomX,
          y: mesh.position.y + randomY,
          duration: duration,
          ease: "power2.in",
        },
        delay
      );

      timeline.to(
        mesh.rotation,
        {
          z: randomRotation,
          duration: duration,
          ease: "power2.in",
        },
        delay
      );

      timeline.to(
        mesh.scale,
        {
          x: 0,
          y: 0,
          z: 0,
          duration: duration,
          ease: "power2.in",
        },
        delay
      );

      timeline.to(
        mat,
        {
          opacity: 0,
          duration: duration * 0.9,
          ease: "power2.in",
          onUpdate: () => {
            mat.needsUpdate = true;
          },
        },
        delay
      );
    });
  }

  dispose() {
    // Dispose all meshes, geometries, and materials
    this.meshes.forEach((mesh) => {
      if (mesh.geometry) {
        mesh.geometry.dispose();
      }
    });

    // Dispose all line materials
    this.lineMaterials.forEach((material) => {
      material.dispose();
    });

    // Remove group from scene
    this.experience.scene.remove(this.group);

    // Clear arrays
    this.meshes = [];
    this.lineMaterials = [];
  }
}
